<template>
  <div
    class="filter"
    :class="{
      'filter--disabled': disabled,
    }"
  >
    <button
      ref="filterHeader"
      class="filter__header action"
      :class="{ 'filter__header--open': open }"
      @click="toggleOpen(null)"
    >
      <div class="action__inner pl-1 pl-md-0 pr-1 pr-md-0">
        <div class="flex-grow-1">
          <p
            class="d-flex align-center mb-0 mb-md-0-4 text-small text-no-wrap text-md-base w-md-600"
          >
            <icon
              v-if="withIcon"
              :name="name"
              class="filter__header__icon mr-0-6"
            />
            <span>
              {{ $t(`search.filter.${name}.title`) }}
            </span>
          </p>
          <p class="d-none d-md-block text-small text-color-secondary-2 mb-0">
            <!-- <a
              v-if="open"
              href="#"
              class="link-unroll"
              :class="{
                'text-color-secondary-2 opacity-50 link--disable': multiple
                  ? value.length === 0
                  : !value.id || value.id === '',
              }"
              @click.prevent="reset"
              >{{ $t('search.filter.reset') }}</a
            > -->
            <span v-if="disabled">
              {{ disabledInfo }}
            </span>
            <span v-else-if="isNotEmpty">
              {{
                translateLabel
                  ? $t(`product.filters.type.${selectedFilters}`)
                  : selectedFilters
              }}
            </span>
            <span v-else>{{ $t(`search.filter.${name}.all`) }}</span>
          </p>
        </div>

        <div class="p-relative lh-1 pl-0-6 d-flex align-center justify-end">
          <p
            class="text-small text-right text-color-secondary-2 d-md-none mb-0 pl-1 lh-1-2"
          >
            <span v-if="disabled">
              {{ disabledInfo }}
            </span>

            <span v-else-if="isNotEmpty">
              <span v-if="multiple && value.length > 3">
                {{
                  $t('search.filter.selected', {
                    number: value.length,
                  })
                }}
              </span>
              <span v-else>
                {{
                  translateLabel
                    ? $t(`product.filters.type.${selectedFilters}`)
                    : selectedFilters
                }}
              </span>
            </span>

            <span v-else>{{ $t(`search.filter.${name}.all`) }}</span>
          </p>
          <icon-carret-down v-if="!disabled" />
          <p
            v-if="!disabled"
            class="filter__header__action text-small mb-0 text-color-secondary-2"
          >
            {{ open ? $t('action.close') : $t('action.open') }}
          </p>
        </div>
      </div>
    </button>

    <transition @enter="enter" @leave="leave">
      <div v-if="open">
        <div
          class="filter__content pl-1 pl-md-0 pr-1 pr-md-0"
          :class="`filter__content--${updatedAt} ${
            multiple ? 'filter__content--multiple' : ''
          }`"
        >
          <!-- <a
          href="#"
          class="link-unroll text-small d-block d-md-none mb-1"
          :class="{
            'text-color-secondary-2 opacity-50 link--disable': multiple
              ? value.length === 0
              : !value.id || value.id === '',
          }"
          @click.prevent="reset"
          >{{ $t('search.filter.reset') }}</a
        > -->

          <component
            :is="filterComponent()"
            v-if="!multiple"
            :id="`filter-${name}-all`"
            :name="`filter-${name}`"
            :value="''"
            :checked="!value.id || value.id === ''"
            class="text-small filter__content__all"
            @input="reset"
          >
            <span>
              {{ $t(`search.filter.${name}.all`) }}
            </span>
          </component>

          <component
            :is="filterComponent()"
            v-for="item in filters"
            :id="`filter-${name}-${item.id}`"
            :key="item.id"
            :name="`filter-${name}`"
            :value="item.id"
            :checked="
              multiple
                ? value.some((i) => i.id === item.id)
                : value.id && value.id === item.id
            "
            class="text-small"
            @input="onInput(item)"
          >
            <span>{{
              translateLabel
                ? $t(`product.filters.type.${item.label}`)
                : item.label
            }}</span>
            <span v-if="item.count">({{ item.count }})</span>
            <span v-if="facetLoading">&nbsp;</span>
          </component>
        </div>
        <div v-if="multiple && filters.length > 0" class="filter__actions">
          <PrimaryButton
            :text="$tc('search.filter.apply', 1)"
            align="center"
            @click="updateValues()"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Icon from '@/components/icons/Icon'
import IconCarretDown from '@/components/icons/CarretDown'
import InputCheckbox from '@/components/form/InputCheckbox'
import InputRadio from '@/components/form/InputRadio'
import PrimaryButton from '@/components/buttons/Primary'

export default {
  name: 'FilterDefault',
  components: {
    Icon,
    IconCarretDown,
    InputCheckbox,
    InputRadio,
    PrimaryButton,
  },
  props: {
    value: { type: [Array, Object], required: true },
    name: { type: String, required: true },
    withIcon: { type: Boolean, default: false },
    filters: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    facetLoading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    disabledInfo: { type: String, default: '' },
    translateLabel: { type: Boolean, default: false },
    updatedAt: { type: Number, default: Date.now() }, // Used to force re-rendering of the filter when updated
  },
  data: () => ({
    open: false,
  }),
  computed: {
    isNotEmpty() {
      if (this.multiple) {
        return this.value.length > 0
      }

      return this.value.id && this.value.id !== ''
    },
    selectedFilters() {
      if (this.multiple) {
        return this.value.map((v) => v.label).join(', ')
      }

      return this.value.label
    },
  },
  methods: {
    toggleOpen(force = null, track = true) {
      this.open = force !== null ? force : !this.open
      this.$refs.filterHeader.blur()

      if (track) {
        this.$track.click(this.open ? 'open_filter' : 'close_filter', {
          filter_name: this.name,
        })
      }
    },
    filterComponent() {
      if (this.multiple) {
        return InputCheckbox
      }

      return InputRadio
    },
    isActive(group, id) {
      if (this.multiple) {
        return this.value.some((v) => v.group === group && v.item.id === id)
      }

      return group === this.value.group && value === this.value.item.id
    },
    onInput(item) {
      if (this.multiple) {
        return this.updateValue(item)
      }

      this.$emit('input', item)
      this.$track.filter(this.name, item.id, { activate: true })
    },
    updateValue(item) {
      const a = [].concat(this.value)
      const index = a.findIndex((el) => el.id === item.id)

      if (index > -1) {
        a.splice(index, 1)
        // this.$track.filter(this.name, item.id, { activate: false })
      } else {
        a.push(item)
        // this.$track.filter(this.name, item.id, { activate: true })
      }

      this.$emit('input', a)
    },
    updateValues() {
      this.$emit('validate')
    },
    reset(ev) {
      if (ev.stopPropagation) {
        ev.stopPropagation()
      }

      this.$track.click('filter_reset', { filter_group: this.name })

      if (this.multiple) {
        return this.$emit('input', [])
      }

      return this.$emit('input', {})
    },
    async enter(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.from(el, {
        height: 0,
        opacity: 0,
        marginTop: 0,
        marginBottom: 0,
        duration: 0.3,
        ease: 'power2.out',
        onComplete() {
          el.style = ''
          done()
        },
      })
    },
    async leave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      gsap.to(el, {
        height: 0,
        opacity: 0,
        marginTop: 0,
        marginBottom: 0,
        duration: 0.4,
        ease: 'power2.out',
        onComplete() {
          done()
        },
      })
    },
  },
}
</script>

<style lang="scss">
.filter {
  border-bottom: 1px solid var(--secondary-color-4);

  .filter__header {
    background: none;
    padding: 0 0 calc(var(--spacing) * 0.6);
    font-weight: 400;
    text-align: left;
    width: 100%;

    @include mq($from: tablet) {
      padding: 0 0 calc(var(--spacing) * 1.5);
    }

    .action__inner {
      padding-top: calc(var(--spacing) * 0.6);

      @include mq($from: tablet) {
        padding-top: calc(var(--spacing) * 1.5);
        align-items: flex-start;
      }
    }

    .icon {
      width: rem(30px);
      height: rem(30px);
      transition: all 0.3s;
    }

    .filter__header__icon {
      width: rem(20px);
      height: rem(20px);
    }

    &__action {
      position: absolute;
      top: 50%;
      right: 100%;
      transform: translate(10%, -50%);
      pointer-events: none;
      opacity: 0;
      transition: all 0.3s;
      margin-top: rem(-3px);
      margin-right: rem(-15px);
      padding: calc(var(--spacing) * 0.6);
      background: var(--tertiary-color);
    }

    @include on-hover-and-focus {
      .filter__header__action {
        transform: translate(0, -50%);
        opacity: 1;
      }
    }

    &--open {
      .icon--carret-down {
        transform: rotate(180deg);
      }
    }
  }

  .filter__content {
    overflow: hidden;
    padding-bottom: var(--spacing);

    @include mq($until: tablet) {
      display: flex;
      flex-wrap: wrap;
      gap: calc(var(--spacing) * 0.6);

      > * {
        flex: 0 1 calc(50% - calc(var(--spacing) * 0.6));
      }

      .filter__content__all {
        flex: 0 1 100%;
      }
    }

    .input {
      margin-bottom: 0;

      @include mq($from: tablet) {
        margin-bottom: calc(var(--spacing) * 0.8);
      }

      label {
        white-space: nowrap;
        padding-left: calc(var(--spacing) * 1.6);
      }
    }

    @include mq($until: tablet) {
      position: relative;
      z-index: 1;
    }
  }

  .filter__content--multiple {
    display: flex;
    flex-wrap: wrap;
    gap: calc(var(--spacing) * 0.6);

    > * {
      flex: 0 1 calc(33% - calc(var(--spacing) * 0.6));

      @include mq($from: tablet) {
        flex: 0 1 calc(50% - calc(var(--spacing) * 0.6));
      }
    }

    .input {
      margin-bottom: 0;
    }
  }

  .filter__actions {
    padding: 0 var(--spacing) var(--spacing);

    @include mq($from: tablet) {
      padding: 0 0 var(--spacing);
    }
  }

  &--disabled {
    .filter__header {
      cursor: default;
      pointer-events: none;
      opacity: 0.5;
    }
  }
}
</style>
